"use client";
import { useState, useEffect, useMemo } from "react";
import {
  Wallet,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  ArrowUpRight,
  ArrowDownLeft,
  QrCode,
  X,
  ExternalLink,
  Users,
  Search,
  Inbox,
  ShoppingBag,
  ChevronDown,
  AlertTriangle,
  Clock,
} from "lucide-react";
import QRCodeLib from "qrcode";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { getBalance, getRecentTransactions } from "../services/authapi";
import toast, { Toaster } from "react-hot-toast";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase";

interface Transaction {
  id: string;
  type: "sent" | "received";
  amount: number;
  from: string;
  to: string;
  date: string;
  hash: string;
  senderEmail?: string | null;
  paymentMethod?: string | null;
  sourceLabel?: string | null;
  invoiceId?: string | null;
  // Checkout-only: a short summary of what was purchased (e.g.
  // "Premium Hoodie ×2 + 1 more") and the underlying line items.
  productSummary?: string | null;
  lineItems?: {
    name?: string;
    description?: string | null;
    image_url?: string | null;
    quantity?: number;
    unit_amount?: number;
  }[] | null;
  // Collect-page fee fields. `amount` is already the seller's net
  // (gross minus fee), so render `feeAmount` as a separate small line —
  // never subtract again.
  feeAmount?: number | null;
  feePct?: number | null;
  feeFlat?: number | null;
  grossAmount?: number | null;
  // Lifecycle / held-deposit context. See Dashboard.tsx for full
  // semantics. Held = funds received in the pool wallet but not yet
  // swept to the seller (e.g. amount mismatch, pending manual review).
  // awaiting_approval / rejected = Bridge bank deposits sitting in a
  // CashTap custody wallet: awaiting admin approval, or declined.
  // release_failed = approved release that didn't land on chain; still
  // in progress from the user's side, so it renders like awaiting_approval.
  status?:
    | "completed"
    | "held"
    | "pending"
    | "awaiting_approval"
    | "rejected"
    | "release_failed"
    | null;
  flagReason?: string | null;
  expectedAmount?: number | null;
  shortfall?: number | null;
  // Operator-flagged invalid record (e.g. funds arrived on a chain CashTap
  // can't spend, like USDC on Ethereum mainnet). Shown struck-through; it is
  // NOT part of the spendable Base balance.
  invalid?: boolean;
  invalidReason?: string | null;
  explorerLink?: string | null;
}

// Display-only label for the transaction-history payment-method tag. Shows the
// CashApp-over-Lightning rail as "CASHAPP" while leaving the stored value
// (`lightning-cashapp`) untouched everywhere else — filters and search keep
// using the raw id.
const displayPaymentMethod = (m?: string | null): string =>
  (m || "").toLowerCase() === "lightning-cashapp" ? "CASHAPP" : (m || "");

// Source-category filters for the transaction list. Each maps a filter id
// to a dropdown label and a predicate over the tx's `sourceLabel` (the
// human tag the backend writes for each record). "transfer" catches
// plain on-chain transfers/deposits that carry no Collect/checkout tag.
const SOURCE_FILTERS: {
  id: string;
  label: string;
  match: (sourceLabel: string | null | undefined) => boolean;
}[] = [
  { id: "all", label: "All types", match: () => true },
  {
    id: "transfer",
    label: "Transfers & deposits",
    match: (l) => !l || l === "Deposit" || l === "Transfer" || l === "Bank Deposit",
  },
  { id: "checkout", label: "Checkout payments", match: (l) => l === "Checkout" },
  { id: "request", label: "Payment links", match: (l) => l === "Payment Link" },
  { id: "buymeCoffee", label: "Tips jar", match: (l) => l === "Tip Jar" },
  { id: "donation", label: "Donation boxes", match: (l) => l === "Donation" },
  { id: "manual", label: "Invoices", match: (l) => l === "Invoice" },
];

// Only checkout records carry the "Checkout" source label — used to gate
// the per-row "View" details button + modal.
function isCheckoutTx(tx: Transaction): boolean {
  return tx.sourceLabel === "Checkout";
}

export function WalletPage() {
  const [showBalance, setShowBalance] = useState(true);
  const [showQRCode, setShowQRCode] = useState(false);
  const [qrCodeDataURL, setQRCodeDataURL] = useState<string>("");
  const [walletBalance, setWalletBalance] = useState<string>("0");
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [txFilter, setTxFilter] = useState<"all" | "received" | "sent">("all");
  const [txSearch, setTxSearch] = useState<string>("");
  const [sourceFilter, setSourceFilter] = useState<string>("all");
  const [methodFilter, setMethodFilter] = useState<string>("all");
  // The checkout tx whose product details are shown in the modal (null = closed).
  const [viewTx, setViewTx] = useState<Transaction | null>(null);
  const [addressToUsername, setAddressToUsername] = useState<{
    [address: string]: string | undefined;
  }>({});
  // NEW: Store profile photos by wallet address
  const [addressToPhotoURL, setAddressToPhotoURL] = useState<{
    [address: string]: string | null;
  }>({});
  const [loadingBalance, setLoadingBalance] = useState<boolean>(false);
  const [loadingTransactions, setLoadingTransactions] =
    useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const user = useSelector((state: RootState) => state.user) as any;
  const walletAddress = (user?.smartWallet || "").toLowerCase();
  const walletAddressWithoutLowerCase = (user?.smartWallet || "")
  const adminWallet = import.meta.env.VITE_Admin_Wallet?.toLowerCase() || "";

  useEffect(() => {
    const fetchBalance = async () => {
      if (!walletAddress) {
        setError("No wallet address found for the user.");
        toast.error("No wallet address found for the user.");
        return;
      }

      setLoadingBalance(true);
      try {
        const ethBalance = await getBalance(walletAddressWithoutLowerCase);
        console.log("Fetched ETH balance:", ethBalance);
        setWalletBalance(ethBalance);
        setError(null);
      } catch (err: any) {
        console.error("Error fetching ETH balance:", err.message);
        toast.error("Failed to fetch wallet balance. Please try again.");
        setError("Failed to fetch wallet balance: " + err.message);
      } finally {
        setLoadingBalance(false);
      }
    };

    fetchBalance();
  }, [walletAddress]);

  const fetchTransactions = async (silent = false) => {
    if (!walletAddress) {
      setError("No wallet address found for the user.");
      if (!silent) toast.error("No wallet address found for the user.");
      return;
    }

    setLoadingTransactions(true);
    try {
      const txs = await getRecentTransactions(walletAddressWithoutLowerCase);
      // Filter out any transactions where the admin wallet is the recipient (fee payments)
      const filteredTxs = txs.filter((tx: Transaction) => {
        if (tx.type === "sent") return tx.to.toLowerCase() !== adminWallet;
        return true;
      });
      setTransactions(filteredTxs);
      setError(null);
      if (silent) toast.success("Transactions refreshed");
    } catch (err: any) {
      console.error("Error fetching transactions:", err.message);
      toast.error("Failed to fetch transactions. Please try again.");
      setError("Failed to fetch transactions: " + err.message);
    } finally {
      setLoadingTransactions(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [walletAddress, adminWallet]);

  // Fetch usernames AND profile photos
  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const usersSnap = await getDocs(collection(db, "users"));
        const vendorsSnap = await getDocs(collection(db, "vendors"));
        const nameMap: { [address: string]: string | undefined } = {};
        const photoMap: { [address: string]: string | null } = {};

        usersSnap.forEach((doc) => {
          const data = doc.data();
          if (data.wallet) {
            const lower = data.wallet.toLowerCase();
            nameMap[lower] = data.username;
            photoMap[lower] = data.photoURL || null;
          }
        });

        vendorsSnap.forEach((doc) => {
          const data = doc.data();
          if (data.wallet) {
            const lower = data.wallet.toLowerCase();
            nameMap[lower] = data.name;
            photoMap[lower] = null; // vendors usually have no photo
          }
        });

        setAddressToUsername(nameMap);
        setAddressToPhotoURL(photoMap);
      } catch (err: any) {
        console.error("Error fetching profiles:", err.message);
        toast.error("Failed to fetch profiles. Please try again.");
      }
    };

    fetchProfiles();
  }, []);

  useEffect(() => {
    if (walletAddress && showQRCode) {
      const generateQRCode = async () => {
        try {
          const canvas = document.createElement("canvas");
          await QRCodeLib.toCanvas(canvas, walletAddress, {
            width: 256,
            margin: 2,
            color: {
              dark: "#61CE70",
              light: "#FFFFFF",
            },
          });

          const ctx = canvas.getContext("2d");
          if (ctx) {
            ctx.font = "bold 36px Arial";
            ctx.fillStyle = "#000000";
            ctx.globalAlpha = 0.5;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";

            ctx.globalAlpha = 1.0;

            const dataURL = canvas.toDataURL("image/png");
            setQRCodeDataURL(dataURL);
          }
        } catch (error) {
          console.error("Error generating QR code:", error);
          toast.error("Failed to generate QR code.");
        }
      };
      generateQRCode();
    }
  }, [walletAddress, showQRCode]);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const handleRefresh = async () => {
    if (!walletAddress) {
      setError("No wallet address found for the user.");
      toast.error("No wallet address found for the user.");
      return;
    }

    setLoadingBalance(true);
    try {
      const ethBalance = await getBalance(walletAddressWithoutLowerCase);
      console.log("Refreshed ETH balance:", ethBalance);
      setWalletBalance(ethBalance);
      setError(null);
      toast.success("Balance refreshed!");
    } catch (err: any) {
      console.error("Error refreshing ETH balance:", err.message);
      toast.error("Failed to refresh wallet balance.");
      setError("Failed to refresh wallet balance: " + err.message);
    } finally {
      setLoadingBalance(false);
    }
  };

  const truncateAddress = (address: string): string => {
    if (!address) return "";
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatAmount = (amount: number): string => {
    if (!isFinite(amount)) return "0";
    return parseFloat(amount.toFixed(2)).toString();
  };

  // What landed in the seller's wallet, floored to 2 decimals so we never
  // overstate balance. e.g., 0.195 USDC swept on chain → "0.19" displayed.
  const formatNetAmount = (amount: number): string => {
    if (!isFinite(amount)) return "0.00";
    return (Math.floor(amount * 100) / 100).toFixed(2);
  };

  // Standard rounding for fee display so 0.305 → "0.31".
  const formatFee = (amount: number): string => {
    if (!isFinite(amount)) return "0.00";
    return amount.toFixed(2);
  };

  const getCounterpartyDisplay = (tx: Transaction): string => {
    if (tx.type === "received" && tx.senderEmail) return tx.senderEmail;
    const counterparty =
      tx.type === "received" ? tx.from.toLowerCase() : tx.to.toLowerCase();
    return addressToUsername[counterparty] || truncateAddress(counterparty);
  };

  // NEW: Get counterparty profile photo
  const getCounterpartyPhoto = (tx: Transaction): string | null => {
    const counterparty =
      tx.type === "received" ? tx.from.toLowerCase() : tx.to.toLowerCase();
    return addressToPhotoURL[counterparty] || null;
  };

  const viewOnBlockExplorer = (hash: string, url?: string | null) => {
    // Prefer the stored explorer link when present (invalid/wrong-chain
    // records carry an etherscan.io link; basescan would 404 for those).
    window.open(url || `https://basescan.org/tx/${hash}`, "_blank");
  };

  // Payment methods actually present in the loaded transactions — drives
  // the Method filter dropdown so we only ever offer methods that exist.
  const availableMethods = useMemo(() => {
    const set = new Set<string>();
    for (const t of transactions) if (t.paymentMethod) set.add(t.paymentMethod);
    return Array.from(set).sort();
  }, [transactions]);

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: "#0F1419",
            color: "#fff",
            border: "1px solid #374151",
            borderRadius: "12px",
            fontSize: "14px",
          },
          success: {
            iconTheme: {
              primary: "#61CE70",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#EF4444",
              secondary: "#fff",
            },
          },
        }}
      />
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
          My Wallet
        </h1>
        <p className="text-gray-400 text-lg">
          Manage your wallet balance and transactions
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 p-4 rounded-2xl">
          {error}
        </div>
      )}

      {/* Wallet Card */}
      <div className="bg-gradient-to-br from-[#61CE70] via-[#55C165] to-[#50B060] rounded-3xl p-6 sm:p-8 mb-8 text-white shadow-2xl shadow-[#61CE70]/20 relative overflow-hidden">
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-32 h-32 sm:w-40 sm:h-40 bg-white/10 rounded-full -translate-y-16 sm:-translate-y-20 translate-x-16 sm:translate-x-20"></div>
        <div className="absolute bottom-0 left-0 w-24 h-24 sm:w-32 sm:h-32 bg-white/5 rounded-full translate-y-12 sm:translate-y-16 -translate-x-12 sm:-translate-x-16"></div>

        {/* Balance + Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4 sm:gap-0 relative z-10">
          <div className="flex items-center space-x-3">
            <div className="p-3 sm:p-4 bg-white/20 backdrop-blur-sm rounded-2xl">
              <Wallet className="w-6 h-6 sm:w-8 sm:h-8" />
            </div>
            <p className="text-3xl sm:text-4xl md:text-5xl font-bold">
              {loadingBalance
                ? "Loading..."
                : showBalance
                  ? `${formatAmount(parseFloat(walletBalance))} USD`
                  : "••••••"}
            </p>
          </div>

          {/* Eye + QR buttons */}
          <div className="flex items-center space-x-2 self-start sm:self-auto">
            <button
              onClick={() => setShowBalance(!showBalance)}
              className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-colors"
            >
              {showBalance ? (
                <Eye className="w-4 h-4 sm:w-5 sm:h-5" />
              ) : (
                <EyeOff className="w-4 h-4 sm:w-5 sm:h-5" />
              )}
            </button>
            <button
              onClick={() => setShowQRCode(true)}
              className="p-2 sm:p-3 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-colors"
            >
              <QrCode className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>

        {/* Address */}
        <div className="mb-6 relative z-10">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
            <div className="flex items-center space-x-2">
              <span className="text-sm opacity-90">Address:</span>
              <span className="font-mono text-xs sm:text-sm break-all">
                {truncateAddress(walletAddress)}
              </span>
              <button
                onClick={() => copyToClipboard(walletAddress)}
                className="p-1 hover:bg-white/20 rounded transition-colors"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 relative z-10">
          <button
            onClick={handleRefresh}
            className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-colors text-sm sm:text-base"
          >
            <RefreshCw className="w-4 h-4" />
            <span>{loadingBalance ? "Refreshing..." : "Refresh"}</span>
          </button>
          <button
            onClick={() => copyToClipboard(walletAddress)}
            className="flex items-center justify-center space-x-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-colors text-sm sm:text-base"
          >
            <Copy className="w-4 h-4" />
            <span>Copy Address</span>
          </button>
        </div>
      </div>

      {/* QR Code Modal */}
      {showQRCode && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl p-8 max-w-sm w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-white">Wallet QR Code</h3>
              <button
                onClick={() => setShowQRCode(false)}
                className="p-2 hover:bg-gray-700/50 rounded-xl transition-colors"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            </div>

            <div className="text-center space-y-4">
              <div className="bg-white p-4 rounded-2xl inline-block">
                <img
                  src={qrCodeDataURL}
                  alt="Wallet QR Code with CashTap"
                  className="w-48 h-48"
                />
              </div>

              <p className="text-gray-400 text-xs">
                Scan this QR code to send funds to your wallet
              </p>
              <p className="text-red-400 text-xs">
                Only Fund the wallet with USDC (Base network)
              </p>
            </div>
          </div>
        </div>
      )}

      {/* All Transactions */}
      <section className="bg-gradient-to-br from-[#0F1419] to-[#1A1F2B] border border-gray-700/50 rounded-3xl backdrop-blur-sm overflow-hidden">
        {/* Header */}
        <header className="px-5 sm:px-7 pt-6 pb-5 border-b border-gray-800/60">
          <div className="flex items-center justify-between gap-4 mb-5">
            <div className="min-w-0">
              <h3 className="text-lg sm:text-xl font-bold text-white tracking-tight">
                All Transactions
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {loadingTransactions
                  ? "Loading…"
                  : `${transactions.length} ${transactions.length === 1 ? "transaction" : "transactions"}`}
              </p>
            </div>
            <button
              onClick={() => fetchTransactions(true)}
              disabled={loadingTransactions}
              aria-label="Refresh transactions"
              className="shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gray-800/50 hover:bg-gray-700/60 active:scale-95 text-gray-300 hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${loadingTransactions ? "animate-spin" : ""}`} />
            </button>
          </div>

          {/* Filters + Search */}
          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="inline-flex items-center bg-gray-900/60 border border-gray-800/80 rounded-xl p-1 w-full sm:w-auto">
                {(["all", "received", "sent"] as const).map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setTxFilter(opt)}
                    className={`flex-1 sm:flex-none px-4 h-9 rounded-lg text-xs sm:text-sm font-medium capitalize transition-all ${
                      txFilter === opt
                        ? "bg-[#61CE70] text-black shadow-sm"
                        : "text-gray-400 hover:text-white"
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                <input
                  type="text"
                  value={txSearch}
                  onChange={(e) => setTxSearch(e.target.value)}
                  placeholder="Search sender email, name, product, or tx hash…"
                  className="w-full h-10 pl-10 pr-9 bg-gray-900/60 border border-gray-800/80 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-[#61CE70]/50 focus:bg-gray-900/80 transition-colors"
                />
                {txSearch && (
                  <button
                    onClick={() => setTxSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-white"
                    aria-label="Clear search"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Type + payment-method dropdowns */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1 sm:max-w-[260px]">
                <select
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  aria-label="Filter by type"
                  className="w-full h-10 pl-3 pr-9 appearance-none bg-gray-900/60 border border-gray-800/80 rounded-xl text-sm text-white focus:outline-none focus:border-[#61CE70]/50 focus:bg-gray-900/80 transition-colors cursor-pointer"
                >
                  {SOURCE_FILTERS.map((s) => (
                    <option key={s.id} value={s.id} className="bg-gray-900">
                      {s.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
              </div>

              <div className="relative flex-1 sm:max-w-[260px]">
                <select
                  value={methodFilter}
                  onChange={(e) => setMethodFilter(e.target.value)}
                  aria-label="Filter by payment method"
                  disabled={availableMethods.length === 0}
                  className="w-full h-10 pl-3 pr-9 appearance-none bg-gray-900/60 border border-gray-800/80 rounded-xl text-sm text-white focus:outline-none focus:border-[#61CE70]/50 focus:bg-gray-900/80 transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="all" className="bg-gray-900">
                    All payment methods
                  </option>
                  {availableMethods.map((m) => (
                    <option key={m} value={m} className="bg-gray-900">
                      {m}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
              </div>

              {(sourceFilter !== "all" || methodFilter !== "all") && (
                <button
                  onClick={() => {
                    setSourceFilter("all");
                    setMethodFilter("all");
                  }}
                  className="h-10 px-3 inline-flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:text-white bg-gray-900/60 border border-gray-800/80 rounded-xl transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                  Clear
                </button>
              )}
            </div>
          </div>
        </header>

        {/* List */}
        {loadingTransactions ? (
          <div className="p-5 sm:p-7 space-y-3">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="flex items-center gap-4 p-4 bg-gray-800/20 rounded-2xl animate-pulse">
                <div className="w-11 h-11 rounded-full bg-gray-700/50" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-32 bg-gray-700/50 rounded" />
                  <div className="h-2.5 w-48 bg-gray-700/40 rounded" />
                </div>
                <div className="h-4 w-16 bg-gray-700/50 rounded" />
              </div>
            ))}
          </div>
        ) : (() => {
          const q = txSearch.trim().toLowerCase();
          const sourceMatcher =
            SOURCE_FILTERS.find((s) => s.id === sourceFilter) || SOURCE_FILTERS[0];
          const filtered = transactions.filter((tx) => {
            if (txFilter !== "all" && tx.type !== txFilter) return false;
            if (!sourceMatcher.match(tx.sourceLabel)) return false;
            if (methodFilter !== "all" && (tx.paymentMethod || "") !== methodFilter)
              return false;
            if (!q) return true;
            const counterparty = tx.type === "received" ? tx.from : tx.to;
            return (
              (tx.senderEmail || "").toLowerCase().includes(q) ||
              counterparty.toLowerCase().includes(q) ||
              tx.hash.toLowerCase().includes(q) ||
              (tx.sourceLabel || "").toLowerCase().includes(q) ||
              (tx.paymentMethod || "").toLowerCase().includes(q) ||
              (tx.productSummary || "").toLowerCase().includes(q) ||
              getCounterpartyDisplay(tx).toLowerCase().includes(q)
            );
          });

          if (filtered.length === 0) {
            return (
              <div className="px-5 sm:px-7 py-16 flex flex-col items-center text-center">
                <div className="w-14 h-14 rounded-full bg-gray-800/60 flex items-center justify-center mb-4">
                  <Inbox className="w-6 h-6 text-gray-500" />
                </div>
                <p className="text-white font-medium">
                  {transactions.length === 0 ? "No transactions yet" : "No matches found"}
                </p>
                <p className="text-gray-500 text-sm mt-1 max-w-xs">
                  {transactions.length === 0
                    ? "Once you receive or send funds, your activity will appear here."
                    : "Try a different keyword or filter."}
                </p>
              </div>
            );
          }

          return (
            <ul className="divide-y divide-gray-800/50">
              {filtered.map((tx: Transaction) => {
                const isReceived = tx.type === "received";
                const isHeld = tx.status === "held";
                const isAwaiting = tx.status === "awaiting_approval";
                const isRejected = tx.status === "rejected";
                const isReleaseFailed = tx.status === "release_failed";
                const invalid = tx.invalid === true;
                const photo = getCounterpartyPhoto(tx);
                const accent = invalid || isRejected
                  ? "text-gray-500"
                  : isHeld || isAwaiting || isReleaseFailed
                  ? "text-amber-400"
                  : isReceived
                    ? "text-[#61CE70]"
                    : "text-red-400";
                const accentBg = invalid || isRejected
                  ? "bg-gray-500/10"
                  : isHeld || isAwaiting || isReleaseFailed
                  ? "bg-amber-500/10"
                  : isReceived
                    ? "bg-[#61CE70]/10"
                    : "bg-red-500/10";
                const amountClass = invalid
                  ? "text-gray-500 line-through decoration-gray-600"
                  : accent;

                return (
                  <li
                    key={tx.id}
                    className="group px-5 sm:px-7 py-4 hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      {/* Avatar with type indicator */}
                      <div className="relative shrink-0">
                        <div className="w-11 h-11 rounded-full overflow-hidden bg-gray-800 border border-gray-700/60 flex items-center justify-center">
                          {photo ? (
                            <img
                              src={photo}
                              alt={getCounterpartyDisplay(tx)}
                              className="w-full h-full object-cover"
                              onError={(e) => ((e.target as HTMLImageElement).style.display = "none")}
                            />
                          ) : (
                            <Users className="w-5 h-5 text-gray-500" />
                          )}
                        </div>
                        <div
                          className={`absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full ${accentBg} ring-2 ring-[#0F1419] flex items-center justify-center`}
                          aria-label={isReceived ? "Received" : "Sent"}
                        >
                          {isReceived ? (
                            <ArrowDownLeft className={`w-3 h-3 ${accent}`} />
                          ) : (
                            <ArrowUpRight className={`w-3 h-3 ${accent}`} />
                          )}
                        </div>
                      </div>

                      {/* Body */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-white text-base sm:text-lg font-bold tracking-tight truncate">
                            {tx.sourceLabel || (isReceived ? "Deposit" : "Transfer")}
                          </span>
                          {tx.paymentMethod && (
                            <span className="text-[10px] uppercase tracking-wide font-medium text-gray-400 bg-gray-800/70 px-1.5 py-0.5 rounded-md">
                              {displayPaymentMethod(tx.paymentMethod)}
                            </span>
                          )}
                          {isHeld && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 rounded-md"
                              title={tx.flagReason || "Funds held pending manual review"}
                            >
                              Held
                            </span>
                          )}
                          {isAwaiting && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 rounded-md"
                              title="Funds received — waiting for approval"
                            >
                              <Clock className="w-3 h-3" />
                              Awaiting approval
                            </span>
                          )}
                          {isReleaseFailed && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 rounded-md"
                              title="Your deposit is being processed — contact support if this persists."
                            >
                              <Clock className="w-3 h-3" />
                              Processing
                            </span>
                          )}
                          {isRejected && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-red-300 bg-red-500/15 border border-red-500/30 px-1.5 py-0.5 rounded-md"
                              title="This deposit was not approved — contact support"
                            >
                              Not approved
                            </span>
                          )}
                          {invalid && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-amber-300 bg-amber-500/15 border border-amber-500/30 px-1.5 py-0.5 rounded-md"
                              title="This deposit is not part of your spendable balance"
                            >
                              <AlertTriangle className="w-3 h-3" />
                              {tx.invalidReason || "Invalid"}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 truncate mt-1">
                          {isReceived ? "From" : "To"}{" "}
                          <span className="text-gray-200 font-medium">{getCounterpartyDisplay(tx)}</span>
                        </p>
                        {tx.productSummary && (
                          <p
                            className="flex items-center gap-1.5 text-[11px] text-gray-400 truncate mt-1"
                            title={
                              tx.lineItems && tx.lineItems.length
                                ? tx.lineItems
                                    .map(
                                      (li) =>
                                        `${li.name || "Item"}${li.quantity && li.quantity > 1 ? ` ×${li.quantity}` : ""}`,
                                    )
                                    .join(", ")
                                : tx.productSummary
                            }
                          >
                            <ShoppingBag className="w-3 h-3 text-gray-500 shrink-0" />
                            <span className="truncate">{tx.productSummary}</span>
                          </p>
                        )}
                        {isHeld && (
                          <p className="text-[11px] text-amber-200/80 mt-1 leading-snug">
                            Pending review — received ${formatNetAmount(tx.grossAmount ?? tx.amount)} of expected ${formatNetAmount(tx.expectedAmount ?? 0)}
                            {tx.feeAmount != null && tx.feeAmount > 0 && (
                              <> · Net on release ${formatNetAmount(tx.amount)} (after ${formatFee(tx.feeAmount)} fee)</>
                            )}
                          </p>
                        )}
                        {isAwaiting && (
                          <p className="text-[11px] text-amber-200/80 mt-1 leading-snug">
                            Funds received — your deposit is being reviewed and will be added to your balance once approved.
                          </p>
                        )}
                        {isRejected && (
                          <p className="text-[11px] text-red-200/80 mt-1 leading-snug">
                            This deposit was not approved. Contact support@cashtap.cash for details.
                          </p>
                        )}
                        {invalid && (
                          <p className="text-[11px] text-amber-200/80 mt-1 leading-snug">
                            {tx.invalidReason === "Invalid Chain"
                              ? "Received on a different network — not added to your balance. Contact support to recover."
                              : "Flagged invalid — not added to your balance."}
                          </p>
                        )}
                        <div className="flex items-center gap-3 text-[11px] text-gray-500 mt-1">
                          <span>{tx.date}</span>
                          <span className="text-gray-700">•</span>
                          <button
                            onClick={() => copyToClipboard(tx.hash)}
                            className="inline-flex items-center gap-1 hover:text-gray-300 transition-colors"
                            title="Copy tx hash"
                          >
                            {truncateAddress(tx.hash)}
                            <Copy className="w-3 h-3" />
                          </button>
                          <button
                            onClick={() => viewOnBlockExplorer(tx.hash, tx.explorerLink)}
                            className="inline-flex items-center gap-1 hover:text-[#61CE70] transition-colors"
                            title="View on block explorer"
                          >
                            Explorer
                            <ExternalLink className="w-3 h-3" />
                          </button>
                          {isCheckoutTx(tx) && (
                            <button
                              onClick={() => setViewTx(tx)}
                              className="inline-flex items-center gap-1 text-[#61CE70] hover:text-[#7BE88A] font-medium transition-colors"
                              title="View purchase details"
                            >
                              <Eye className="w-3 h-3" />
                              View
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Amount + inline fee. Single horizontal row so the
                          fee deduction sits beside the amount instead of
                          stacking below it — keeps the row visually tight
                          and the fee feels like a direct annotation of
                          what the seller actually netted. */}
                      <div className="shrink-0 text-right">
                        <div className="flex items-baseline justify-end gap-2">
                          <span className={`text-base sm:text-lg font-bold tabular-nums ${amountClass}`}>
                            {isReceived
                              ? `+${formatNetAmount(tx.amount)}`
                              : `−${formatAmount(tx.amount)}`}
                          </span>
                          {isReceived && tx.feeAmount != null && tx.feeAmount > 0 && (
                            <span className="text-[11px] text-red-400 font-semibold tabular-nums whitespace-nowrap">
                              <span className="text-red-400/40 mx-0.5">·</span>
                              −${formatFee(tx.feeAmount)} fee
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] uppercase tracking-wider text-gray-500 mt-0.5">
                          USD
                        </p>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          );
        })()}
      </section>

      {viewTx && (
        <CheckoutDetailsModal
          tx={viewTx}
          payerDisplay={getCounterpartyDisplay(viewTx)}
          onClose={() => setViewTx(null)}
          onExplorer={() => viewOnBlockExplorer(viewTx.hash, viewTx.explorerLink)}
        />
      )}
    </div>
  );
}

// ─── Checkout purchase-details modal ────────────────────────────────────────
// Shown only for checkout transactions (gated by the "View" button). Lays
// out the full cart, payer, method, fee breakdown, and on-chain reference.
function CheckoutDetailsModal({
  tx,
  payerDisplay,
  onClose,
  onExplorer,
}: {
  tx: Transaction;
  payerDisplay: string;
  onClose: () => void;
  onExplorer: () => void;
}) {
  const items = tx.lineItems || [];
  const money = (n: number | null | undefined) =>
    typeof n === "number"
      ? n.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "—";
  const gross = tx.grossAmount ?? tx.amount;
  const fee = tx.feeAmount ?? 0;
  const truncate = (s: string) =>
    s && s.length > 18 ? `${s.slice(0, 10)}…${s.slice(-6)}` : s;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Purchase details"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-md max-h-[88vh] overflow-y-auto rounded-2xl border border-gray-800 bg-[#0F1419] shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-5 py-4 border-b border-gray-800 bg-[#0F1419]">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-[#61CE70]/12 border border-[#61CE70]/25 flex items-center justify-center shrink-0">
              <ShoppingBag className="w-4 h-4 text-[#61CE70]" />
            </div>
            <div className="min-w-0">
              <p className="text-white font-bold text-sm leading-tight">
                Purchase details
              </p>
              <p className="text-[11px] text-gray-500 leading-tight">
                Checkout payment
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="shrink-0 p-2 rounded-lg text-gray-400 hover:text-white hover:bg-white/5 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Amount */}
          <div className="text-center">
            <p className="text-3xl font-bold text-white tabular-nums">
              +${money(tx.amount)}
              <span className="text-sm font-semibold text-gray-500 ml-1.5">USD</span>
            </p>
            <p className="text-[11px] text-gray-500 mt-1">Net received</p>
          </div>

          {/* Items */}
          <div>
            <p className="text-[11px] font-bold tracking-[0.16em] uppercase text-gray-500 mb-2">
              Items
            </p>
            {items.length > 0 ? (
              <ul className="space-y-2.5">
                {items.map((it, i) => {
                  const qty = it.quantity ?? 1;
                  const unit = it.unit_amount ?? 0;
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <div className="relative w-11 h-11 rounded-lg bg-gray-800 border border-gray-700/60 flex items-center justify-center shrink-0 overflow-hidden">
                        {it.image_url ? (
                          <>
                            <img
                              src={it.image_url}
                              alt={it.name || "Product"}
                              loading="lazy"
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                const el = e.target as HTMLImageElement;
                                el.style.display = "none";
                                const sib = el.nextElementSibling as HTMLElement | null;
                                if (sib) sib.style.display = "flex";
                              }}
                            />
                            <span
                              className="absolute inset-0 items-center justify-center text-[11px] font-bold text-gray-300 tabular-nums"
                              style={{ display: "none" }}
                            >
                              ×{qty}
                            </span>
                            {qty > 1 && (
                              <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-[#61CE70] text-black text-[9px] font-bold flex items-center justify-center tabular-nums">
                                ×{qty}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="text-[11px] font-bold text-gray-300 tabular-nums">
                            ×{qty}
                          </span>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-white text-sm font-medium leading-tight truncate">
                          {it.name || "Item"}
                        </p>
                        {it.description && (
                          <p className="text-[11px] text-gray-500 truncate mt-0.5">
                            {it.description}
                          </p>
                        )}
                      </div>
                      <p className="text-sm text-gray-200 font-semibold tabular-nums shrink-0">
                        ${money(unit * qty)}
                      </p>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-gray-500">
                {tx.productSummary || "No itemised products recorded."}
              </p>
            )}
          </div>

          {/* Totals */}
          <div className="pt-3 border-t border-gray-800 space-y-1.5 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Amount paid</span>
              <span className="text-gray-200 tabular-nums">${money(gross)}</span>
            </div>
            {fee > 0 && (
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Platform fee</span>
                <span className="text-red-400 tabular-nums">−${money(fee)}</span>
              </div>
            )}
            <div className="flex items-center justify-between pt-1">
              <span className="text-white font-bold">Net to you</span>
              <span className="text-[#61CE70] font-bold tabular-nums">
                ${money(tx.amount)} USD
              </span>
            </div>
          </div>

          {/* Meta */}
          <div className="pt-3 border-t border-gray-800 space-y-2.5">
            <Row label="Paid by" value={payerDisplay} />
            {tx.paymentMethod && <Row label="Method" value={displayPaymentMethod(tx.paymentMethod)} />}
            <Row label="Date" value={tx.date} />
            {tx.invoiceId && <Row label="Order ID" value={tx.invoiceId} mono />}
            <Row label="Tx hash" value={truncate(tx.hash)} mono />
          </div>

          <button
            onClick={onExplorer}
            className="w-full h-11 inline-flex items-center justify-center gap-2 rounded-xl bg-gray-800/60 hover:bg-gray-700/70 text-white text-sm font-semibold transition-colors"
          >
            View on BaseScan
            <ExternalLink className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-gray-500 shrink-0">{label}</span>
      <span
        className={`text-gray-200 text-right truncate ${mono ? "font-mono text-xs" : ""}`}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}

export default WalletPage;
